import type { Href } from "expo-router";
import { useRouter, useSegments } from "expo-router";
import { useMemo } from "react";
import { Pressable, View } from "react-native";

import { isAdminRole, signOutToHome } from "../lib/auth";
import { useAuth } from "../lib/authContext";
import { NotificationBell } from "./NotificationBell";
import { Avatar } from "./ui/Avatar";
import { Button } from "./ui/Button";

const DARK_PORTAL_SEGMENTS = new Set([
  "(client)",
  "(judge)",
  "(admin)",
  "(admin-rm)",
  "(admin-licensing)",
  "(super-admin)",
]);

function inferHeaderTheme(segments: string[]): "light" | "dark" {
  return segments.some((s) => DARK_PORTAL_SEGMENTS.has(s)) ? "dark" : "light";
}

/**
 * Compact utility bar shown on top of non-student portals (client / judge /
 * campus / admin). Students don't use this header - they have their own
 * `StudentPortalShell` sidebar that surfaces Messages, Connections, profile,
 * etc.
 *
 * Layout: minimal, right-aligned utilities. Items shown depend on role.
 *   - Bell (notifications) - always
 *   - Avatar (identity badge) - always; clickable for student/campus profiles
 *   - Settings (clients) / Security (admins) - role-specific
 *   - Sign out - always
 */
export function AppHeader({ theme: themeProp = "auto" }: { theme?: "light" | "dark" | "auto" }) {
  const segments = useSegments();
  const router = useRouter();
  const { role, user } = useAuth();

  const resolvedTheme = themeProp === "auto" ? inferHeaderTheme(segments as string[]) : themeProp;

  const isAdmin = isAdminRole(role);
  const isClient = role === "client";
  const isStudent = role === "student";
  const isCampus = role === "campus_admin";

  const profileEnabled = isStudent || isCampus;
  const profileHref: Href | null = isStudent
    ? ("/student/profile" as Href)
    : isCampus
      ? ("/campus" as Href)
      : null;

  const avatarUrl = (user?.user_metadata?.avatar_url as string | undefined) ?? null;
  const displayName =
    (user?.user_metadata?.full_name as string | undefined)?.trim() ||
    user?.email?.split("@")[0] ||
    "Account";

  async function onSignOut() {
    await signOutToHome(router);
  }

  const barStyle = useMemo(
    () =>
      resolvedTheme === "dark"
        ? {
            backgroundColor: "transparent" as const,
            borderBottomColor: "rgba(255,255,255,0.08)" as const,
          }
        : {
            backgroundColor: "transparent" as const,
            borderBottomColor: "rgba(28,43,26,0.12)" as const,
          },
    [resolvedTheme]
  );

  const ghostVariant = resolvedTheme === "dark" ? "ghost-dark" : "ghost-light";

  return (
    <View
      className="w-full border-b py-3"
      style={{
        backgroundColor: barStyle.backgroundColor,
        borderBottomColor: barStyle.borderBottomColor,
      }}
    >
      <View className="flex-row items-center justify-end gap-2">
        <NotificationBell theme={resolvedTheme} />

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Profile"
          disabled={!profileEnabled}
          className={!profileEnabled ? "opacity-100" : ""}
          onPress={() => {
            if (!profileEnabled || !profileHref) return;
            router.push(profileHref);
          }}
        >
          <Avatar name={displayName} imageUrl={avatarUrl} size="sm" theme={resolvedTheme} />
        </Pressable>

        {isAdmin ? (
          <Button
            variant={ghostVariant}
            size="sm"
            theme={resolvedTheme}
            onPress={() => router.push("/admin/security" as Href)}
          >
            Security
          </Button>
        ) : null}

        {isClient ? (
          <Button
            variant={ghostVariant}
            size="sm"
            theme={resolvedTheme}
            onPress={() => router.push("/client/settings" as Href)}
          >
            Settings
          </Button>
        ) : null}

        <Button variant={ghostVariant} size="sm" theme={resolvedTheme} onPress={() => void onSignOut()}>
          Sign out
        </Button>
      </View>
    </View>
  );
}
